import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { api, EMBEDDED, ADMIN_LINK_TARGET } from './shopifyEmbed';
import './LocationOrdersPage.css';

// Draft orders built against ONE ship-from location. Shopify's own Create order
// page sums stock across every location (so Fanfinity BV stock shows as
// available for a US order); here the location comes first and every product
// shows only what that location has.

const EMPTY_ADDRESS = { firstName: '', lastName: '', company: '', address1: '', address2: '',
                        city: '', provinceCode: '', zip: '', countryCode: '', phone: '' };
const NO_DISCOUNT   = { type: 'PERCENTAGE', value: '', reason: '' };

function money(amount, currency) {
  if (amount === null || amount === undefined || amount === '') return '—';
  const n = Number(amount);
  try {
    return n.toLocaleString(undefined, { style: 'currency', currency: currency || 'USD' });
  } catch {
    return `${n.toFixed(2)} ${currency || ''}`;
  }
}

function errorText(e) {
  return e.response?.data?.error || e.message;
}

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

let nextKey = 1;

// ── Small pieces ─────────────────────────────────────────────────────────────

function DiscountEditor({ value, onChange, onClose, perUnit }) {
  const d = value || NO_DISCOUNT;
  return (
    <div className="lo-discount-editor">
      <div className="lo-row">
        <select value={d.type} onChange={e => onChange({ ...d, type: e.target.value })}>
          <option value="PERCENTAGE">Percentage</option>
          <option value="FIXED_AMOUNT">{perUnit ? 'Amount (per unit)' : 'Amount'}</option>
        </select>
        <input type="number" min="0" step="0.01" placeholder={d.type === 'PERCENTAGE' ? '%' : '0.00'}
               value={d.value} onChange={e => onChange({ ...d, value: e.target.value })} />
      </div>
      <input placeholder="Reason (shown to customer)" value={d.reason}
             onChange={e => onChange({ ...d, reason: e.target.value })} />
      <div className="lo-row">
        <button className="lo-link" onClick={() => { onChange(null); onClose(); }}>Remove discount</button>
        <button className="lo-btn-secondary lo-small" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

function describeDiscount(d, currency) {
  if (!d || !d.value) return null;
  return d.type === 'PERCENTAGE' ? `${d.value}% off` : `${money(d.value, currency)} off`;
}

function AddressFields({ value, onChange }) {
  const set = k => e => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="lo-address">
      <div className="lo-row">
        <input placeholder="First name" value={value.firstName} onChange={set('firstName')} />
        <input placeholder="Last name" value={value.lastName} onChange={set('lastName')} />
      </div>
      <input placeholder="Company" value={value.company} onChange={set('company')} />
      <input placeholder="Address" value={value.address1} onChange={set('address1')} />
      <input placeholder="Apartment, suite, etc." value={value.address2} onChange={set('address2')} />
      <div className="lo-row">
        <input placeholder="City" value={value.city} onChange={set('city')} />
        <input placeholder="Postal code" value={value.zip} onChange={set('zip')} />
      </div>
      <div className="lo-row">
        <input placeholder="State/province code (e.g. CA)" value={value.provinceCode} onChange={set('provinceCode')} />
        <input placeholder="Country code (e.g. US, BE)" maxLength={2} value={value.countryCode}
               onChange={e => onChange({ ...value, countryCode: e.target.value.toUpperCase() })} />
      </div>
      <input placeholder="Phone" value={value.phone} onChange={set('phone')} />
    </div>
  );
}

function addressLine(a) {
  if (!a) return '';
  return [a.company, a.address1, a.address2, a.city, a.provinceCode, a.zip, a.countryCode].filter(Boolean).join(', ');
}

function stripAddress(a) {
  const out = { ...EMPTY_ADDRESS };
  Object.keys(EMPTY_ADDRESS).forEach(k => { out[k] = (a && a[k]) || ''; });
  return out;
}

// ── Customer card ────────────────────────────────────────────────────────────

function CustomerSection({ customer, onSelect }) {
  const [q,        setQ]        = useState('');
  const [results,  setResults]  = useState([]);
  const [busy,     setBusy]     = useState(false);
  const [creating, setCreating] = useState(false);
  const [form,     setForm]     = useState({ firstName: '', lastName: '', email: '', phone: '', address: { ...EMPTY_ADDRESS } });
  const [error,    setError]    = useState(null);
  const dq = useDebounced(q, 300);

  useEffect(() => {
    if (!dq.trim()) { setResults([]); return; }
    let live = true;
    setBusy(true);
    api.get('/api/location-orders/customers', { params: { q: dq } })
      .then(r => { if (live) setResults(r.data); })
      .catch(e => { if (live) setError(errorText(e)); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [dq]);

  async function createCustomer() {
    setError(null);
    try {
      const r = await api.post('/api/location-orders/customers', form);
      onSelect(r.data);
      setCreating(false); setQ('');
    } catch (e) {
      setError(errorText(e));
    }
  }

  if (customer) {
    return (
      <div className="lo-card">
        <div className="lo-card-head">
          <h2>Customer</h2>
          <button className="lo-link" onClick={() => onSelect(null)}>Remove</button>
        </div>
        <div className="lo-customer-name">{customer.name}</div>
        <div className="lo-muted">{customer.orders} order{customer.orders === 1 ? '' : 's'}</div>
        {customer.email && <div>{customer.email}</div>}
        {customer.phone && <div>{customer.phone}</div>}
        {customer.tags.length > 0 && (
          <div className="lo-tags">{customer.tags.map(t => <span key={t} className="lo-tag">{t}</span>)}</div>
        )}
        {customer.tax_exempt && <div className="lo-warn-text">Tax exempt customer</div>}
      </div>
    );
  }

  return (
    <div className="lo-card">
      <div className="lo-card-head">
        <h2>Customer</h2>
        <button className="lo-link" onClick={() => setCreating(c => !c)}>{creating ? 'Cancel' : 'Create new customer'}</button>
      </div>
      {!creating && (
        <>
          <input placeholder="Search name, email, phone or company" value={q} onChange={e => setQ(e.target.value)} />
          {busy && <div className="lo-muted">Searching…</div>}
          {results.length > 0 && (
            <ul className="lo-results">
              {results.map(c => (
                <li key={c.id} onClick={() => { onSelect(c); setQ(''); setResults([]); }}>
                  <div>{c.name}</div>
                  <div className="lo-muted">{c.email || c.phone || 'no email'}{c.tags.length ? ` · ${c.tags.join(', ')}` : ''}</div>
                </li>
              ))}
            </ul>
          )}
          {!busy && dq.trim() && results.length === 0 && <div className="lo-muted">No customers found.</div>}
        </>
      )}
      {creating && (
        <div className="lo-address">
          <div className="lo-row">
            <input placeholder="First name" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} />
            <input placeholder="Last name" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
          </div>
          <input placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          <input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          <div className="lo-label">Address</div>
          <AddressFields value={form.address} onChange={a => setForm({ ...form, address: a })} />
          <button className="lo-btn-primary lo-small" onClick={createCustomer}>Save customer</button>
        </div>
      )}
      {error && <div className="error">{error}</div>}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function LocationOrdersPage() {
  const [meta,       setMeta]       = useState(null);   // {locations, currencies, payment_terms}
  const [locationId, setLocationId] = useState('');
  const [currency,   setCurrency]   = useState('USD');

  const [q,          setQ]          = useState('');
  const [inStock,    setInStock]    = useState(true);
  const [results,    setResults]    = useState([]);
  const [searching,  setSearching]  = useState(false);
  const [lines,      setLines]      = useState([]);     // {key, custom?, variant_id, title, sku, price, image, available, tracked, quantity, discount}
  const [editingDiscount, setEditingDiscount] = useState(null);  // line key, or 'order'
  const [customDraft, setCustomDraft] = useState(null);
  const [cartonPlan,  setCartonPlan]  = useState(null);   // suggested Master/Inner split per base line
  const [cartonEdits, setCartonEdits] = useState({});     // line key -> {master_qty, inner_qty, unit_qty}
  const [preConversion, setPreConversion] = useState(null); // lines before the last conversion (undo)
  const [converting,  setConverting]  = useState(false);

  const [customer,   setCustomer]   = useState(null);
  const [email,      setEmail]      = useState('');
  const [shipAddr,   setShipAddr]   = useState({ ...EMPTY_ADDRESS });
  const [billSame,   setBillSame]   = useState(true);
  const [billAddr,   setBillAddr]   = useState({ ...EMPTY_ADDRESS });

  const [orderDiscount, setOrderDiscount] = useState(null);
  const [discountCodes, setDiscountCodes] = useState('');
  const [autoDiscounts, setAutoDiscounts] = useState(true);
  const [shipping,   setShipping]   = useState({ mode: 'none', title: '', price: '', handle: '' });
  const [taxExempt,  setTaxExempt]  = useState(false);
  const [note,       setNote]       = useState('');
  const [tags,       setTags]       = useState('');
  const [poNumber,   setPoNumber]   = useState('');

  const [dueLater,   setDueLater]   = useState(false);
  const [termsId,    setTermsId]    = useState('');
  const [issuedOn,   setIssuedOn]   = useState(() => new Date().toISOString().slice(0, 10));
  const [dueOn,      setDueOn]      = useState('');

  const [calc,       setCalc]       = useState(null);
  const [calcError,  setCalcError]  = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [allowOversell, setAllowOversell] = useState(false);

  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoice,    setInvoice]    = useState({ to: '', bcc: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState(null);
  const [result,     setResult]     = useState(null);
  const [recent,     setRecent]     = useState([]);
  const calcSeq = useRef(0);

  const location = meta?.locations.find(l => l.id === locationId);
  const terms    = meta?.payment_terms.find(t => t.id === termsId);

  const loadRecent = useCallback(() => {
    api.get('/api/location-orders/recent').then(r => setRecent(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (EMBEDDED) document.body.classList.add('lo-embedded-body');
    api.get('/api/location-orders/locations')
      .then(r => {
        setMeta(r.data);
        const net30 = r.data.payment_terms.find(t => t.name === 'Net 30') || r.data.payment_terms[0];
        if (net30) setTermsId(net30.id);
      })
      .catch(e => setError(errorText(e)));
    loadRecent();
  }, [loadRecent]);

  // Product search at the chosen location
  const dq = useDebounced(q, 300);
  useEffect(() => {
    if (!locationId || !dq.trim()) { setResults([]); return; }
    let live = true;
    setSearching(true);
    api.get('/api/location-orders/variants', { params: { location_id: locationId, q: dq, in_stock: inStock ? '1' : '0' } })
      .then(r => { if (live) setResults(r.data); })
      .catch(e => { if (live) setError(errorText(e)); })
      .finally(() => { if (live) setSearching(false); });
    return () => { live = false; };
  }, [dq, locationId, inStock]);

  function chooseLocation(id) {
    if (id === locationId) return;
    if (lines.some(l => !l.custom) &&
        !window.confirm('Switch location? Stock will be re-checked against the new location.')) return;
    setLocationId(id); setResults([]); setAllowOversell(false); setResult(null);
  }

  function addVariant(v) {
    setLines(ls => {
      const existing = ls.find(l => l.variant_id === v.id && !l.discount);
      if (existing) return ls.map(l => l === existing ? { ...l, quantity: Number(l.quantity) + 1 } : l);
      return [...ls, { key: nextKey++, variant_id: v.id, title: v.product_title, variant_title: v.variant_title,
                       sku: v.sku, price: v.price, image: v.image, available: v.available, tracked: v.tracked,
                       quantity: 1, discount: null }];
    });
  }

  function addCustom() {
    if (!customDraft.title.trim()) return;
    setLines(ls => [...ls, { key: nextKey++, custom: true, ...customDraft, discount: null }]);
    setCustomDraft(null);
  }

  const updateLine = (key, patch) => setLines(ls => ls.map(l => l.key === key ? { ...l, ...patch } : l));
  const removeLine = key => setLines(ls => ls.filter(l => l.key !== key));

  // ── Master/Inner conversion (same carton sizes as the Dist Orders tool) ──
  const baseLines = lines.filter(l => !l.custom && !l.carton);

  async function suggestCartons() {
    setConverting(true); setError(null);
    try {
      const r = await api.post('/api/location-orders/cartons', {
        location_id: locationId,
        lines: baseLines.map(l => ({ key: l.key, sku: l.sku, quantity: Number(l.quantity) })),
      });
      setCartonPlan(r.data); setCartonEdits({});
    } catch (e) {
      setError(errorText(e));
    } finally {
      setConverting(false);
    }
  }

  const planRow = p => ({ ...p, ...(cartonEdits[p.key] || {}) });
  const planUnits = p => Number(p.master_qty || 0) * (p.master_size || 0) +
                         Number(p.inner_qty || 0) * (p.inner_size || 0) + Number(p.unit_qty || 0);

  function applyCartons() {
    const byKey = Object.fromEntries(cartonPlan.map(p => [p.key, planRow(p)]));
    const out = [];
    lines.forEach(l => {
      const p = byKey[l.key];
      if (!p) { out.push(l); return; }
      // A per-unit fixed discount becomes per carton: $1 off each unit = $40 off a master of 40
      const scaled = size => (l.discount?.type === 'FIXED_AMOUNT' && l.discount.value)
        ? { ...l.discount, value: String(Number(l.discount.value) * size) } : l.discount;
      [['Master', p.master_variant, p.master_size, p.master_qty], ['Inner', p.inner_variant, p.inner_size, p.inner_qty]]
        .forEach(([tier, v, size, q]) => {
          if (!v || !(Number(q) > 0)) return;
          out.push({ key: nextKey++, variant_id: v.id, title: v.product_title, variant_title: v.variant_title,
                     sku: v.sku, price: v.price, image: v.image || l.image, tracked: l.tracked,
                     available: l.available === null || l.available === undefined ? l.available : Math.floor(l.available / size),
                     quantity: Number(q), discount: scaled(size),
                     carton: { tier, size, base_variant_id: l.variant_id, base_sku: l.sku } });
        });
      if (Number(p.unit_qty) > 0) out.push({ ...l, key: nextKey++, quantity: Number(p.unit_qty) });
    });
    setPreConversion(lines); setLines(out); setCartonPlan(null); setCartonEdits({});
  }

  function undoConversion() {
    if (!window.confirm('Go back to the lines as they were before converting? Edits made since will be lost.')) return;
    setLines(preConversion); setPreConversion(null);
  }

  function selectCustomer(c) {
    setCustomer(c);
    if (!c) return;
    setEmail(c.email || '');
    setTaxExempt(!!c.tax_exempt);
    setShipAddr(stripAddress(c.default_address));
    setInvoice(inv => ({ ...inv, to: c.email || '' }));
  }

  // Everything the backend needs, in its whitelisted shape
  const body = useMemo(() => ({
    location_id: locationId,
    currency,
    lines: lines.map(l => l.custom
      ? { custom: true, title: l.title, price: l.price, quantity: l.quantity, taxable: l.taxable,
          requires_shipping: l.requires_shipping, discount: l.discount }
      : { variant_id: l.variant_id, quantity: l.quantity, discount: l.discount }),
    customer_id: customer?.id || null,
    email,
    shipping_address: shipAddr,
    billing_same: billSame,
    billing_address: billAddr,
    order_discount: orderDiscount,
    discount_codes: discountCodes.split(',').map(s => s.trim()).filter(Boolean),
    accept_automatic_discounts: autoDiscounts,
    shipping,
    tax_exempt: taxExempt,
    note,
    tags: tags.split(',').map(s => s.trim()).filter(Boolean),
    po_number: poNumber,
    payment_terms: dueLater && termsId ? { template_id: termsId, issued_on: issuedOn, due_on: dueOn } : null,
  }), [locationId, currency, lines, customer, email, shipAddr, billSame, billAddr, orderDiscount, discountCodes,
       autoDiscounts, shipping, taxExempt, note, tags, poNumber, dueLater, termsId, issuedOn, dueOn]);

  // Live totals (Shopify's own calculation) + stock check at the location
  const calcKey = JSON.stringify({ ...body, note: '', tags: [], po_number: '', payment_terms: null });
  const debouncedCalcKey = useDebounced(calcKey, 500);
  useEffect(() => {
    const b = JSON.parse(debouncedCalcKey);
    if (!b.location_id || b.lines.length === 0 || b.lines.some(l => !(Number(l.quantity) >= 1))) {
      setCalc(null); setCalcError(null); return;
    }
    const seq = ++calcSeq.current;
    setCalculating(true);
    api.post('/api/location-orders/calculate', b)
      .then(r => { if (seq === calcSeq.current) { setCalc(r.data); setCalcError(null); } })
      .catch(e => { if (seq === calcSeq.current) setCalcError(errorText(e)); })
      .finally(() => { if (seq === calcSeq.current) setCalculating(false); });
  }, [debouncedCalcKey]);

  const shortfalls = calc?.shortfalls || [];
  const shortBy = Object.fromEntries(shortfalls.map(s => [s.variant_id, s]));
  const blockedByStock = shortfalls.length > 0 && !allowOversell;
  const canSubmit = location && lines.length > 0 && !submitting && !blockedByStock && !calcError && !calculating;

  async function submit(action) {
    let msg;
    if (action === 'complete') {
      msg = dueLater
        ? `Create the order now with payment due later (${terms?.name})?`
        : 'Create the order and mark it as PAID?';
      msg += `\n\nIt will ship from ${location.name}.`;
      if (!window.confirm(msg)) return;
    }
    setSubmitting(true); setError(null); setResult(null);
    const payload = { ...body, action, allow_oversell: allowOversell };
    if (action === 'invoice') {
      payload.invoice = { ...invoice, bcc: invoice.bcc.split(',').map(s => s.trim()).filter(Boolean) };
    }
    try {
      const r = await api.post('/api/location-orders/create', payload);
      setResult(r.data);
      setInvoiceOpen(false);
      if (!r.data.errors.length) resetOrder();
      loadRecent();
    } catch (e) {
      setError(errorText(e));
      if (e.response?.data?.shortfalls) setCalc(c => c && { ...c, shortfalls: e.response.data.shortfalls });
    } finally {
      setSubmitting(false);
    }
  }

  function resetOrder() {
    setLines([]); setCustomer(null); setEmail(''); setShipAddr({ ...EMPTY_ADDRESS }); setBillAddr({ ...EMPTY_ADDRESS });
    setBillSame(true); setOrderDiscount(null); setDiscountCodes(''); setShipping({ mode: 'none', title: '', price: '', handle: '' });
    setTaxExempt(false); setNote(''); setTags(''); setPoNumber(''); setDueLater(false); setAllowOversell(false);
    setInvoice({ to: '', bcc: '', subject: '', message: '' }); setQ(''); setCalc(null);
    setCartonPlan(null); setCartonEdits({}); setPreConversion(null);
  }

  const cur = calc?.currency || currency;

  return (
    <div className={`lo-page ${EMBEDDED ? 'lo-embedded' : ''}`}>
      <h1>Create order by location</h1>
      <p className="lo-intro">
        Pick where the order ships from first. Products show only that location's available stock, and once
        the order exists it's assigned to that location.
      </p>

      {error && <div className="error">{error}</div>}
      {result && <ResultPanel result={result} onDismiss={() => setResult(null)} />}

      <div className="lo-card lo-location-card">
        <h2>Ship from</h2>
        {!meta && <div className="lo-muted">Loading locations…</div>}
        <div className="lo-location-buttons">
          {meta?.locations.map(l => (
            <button key={l.id} className={`lo-location ${l.id === locationId ? 'active' : ''}`}
                    onClick={() => chooseLocation(l.id)}>{l.name}</button>
          ))}
        </div>
      </div>

      <div className={`lo-grid ${location ? '' : 'lo-disabled'}`}>
        <div className="lo-main">
          {/* Products */}
          <div className="lo-card">
            <div className="lo-card-head">
              <h2>Products</h2>
              <div className="lo-row">
                {preConversion && <button className="lo-link" onClick={undoConversion}>Undo conversion</button>}
                <button className="lo-link" disabled={!location || baseLines.length === 0 || converting || !!cartonPlan}
                        onClick={suggestCartons}>
                  {converting ? 'Working…' : 'Convert to master/inner'}
                </button>
                <button className="lo-link" disabled={!location}
                        onClick={() => setCustomDraft({ title: '', price: '', quantity: 1, taxable: true, requires_shipping: true })}>
                  Add custom item
                </button>
              </div>
            </div>
            <div className="lo-row">
              <input className="lo-grow" disabled={!location} value={q} onChange={e => setQ(e.target.value)}
                     placeholder={location ? `Search products at ${location.name} (title or SKU)` : 'Pick a ship-from location first'} />
              <label className="lo-check">
                <input type="checkbox" checked={inStock} onChange={e => setInStock(e.target.checked)} />
                In stock only
              </label>
            </div>
            {searching && <div className="lo-muted">Searching…</div>}
            {results.length > 0 && (
              <ul className="lo-results lo-product-results">
                {results.map(v => (
                  <li key={v.id} onClick={() => addVariant(v)}>
                    {v.image ? <img src={v.image} alt="" /> : <span className="lo-noimg" />}
                    <div className="lo-grow">
                      <div>{v.product_title}{v.variant_title && <span className="lo-muted"> — {v.variant_title}</span>}</div>
                      <div className="lo-muted">{v.sku}</div>
                    </div>
                    <div className="lo-right">
                      <div>{money(v.price, 'USD')}</div>
                      <Avail v={v} location={location} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {!searching && dq.trim() && location && results.length === 0 && (
              <div className="lo-muted">No {inStock ? 'in-stock ' : ''}products match at {location.name}.</div>
            )}

            {customDraft && (
              <div className="lo-custom">
                <div className="lo-row">
                  <input className="lo-grow" placeholder="Item name" value={customDraft.title}
                         onChange={e => setCustomDraft({ ...customDraft, title: e.target.value })} />
                  <input type="number" min="0" step="0.01" placeholder={`Price (${currency})`} value={customDraft.price}
                         onChange={e => setCustomDraft({ ...customDraft, price: e.target.value })} />
                  <input type="number" min="1" className="lo-qty" value={customDraft.quantity}
                         onChange={e => setCustomDraft({ ...customDraft, quantity: e.target.value })} />
                </div>
                <div className="lo-row">
                  <label className="lo-check"><input type="checkbox" checked={customDraft.taxable}
                    onChange={e => setCustomDraft({ ...customDraft, taxable: e.target.checked })} /> Item is taxable</label>
                  <label className="lo-check"><input type="checkbox" checked={customDraft.requires_shipping}
                    onChange={e => setCustomDraft({ ...customDraft, requires_shipping: e.target.checked })} /> Item is a physical product</label>
                  <span className="lo-grow" />
                  <button className="lo-link" onClick={() => setCustomDraft(null)}>Cancel</button>
                  <button className="lo-btn-primary lo-small" onClick={addCustom}>Add item</button>
                </div>
              </div>
            )}

            {cartonPlan && (
              <div className="lo-carton-plan">
                <div className="lo-card-head">
                  <strong>Convert to master/inner cartons</strong>
                  <span className="lo-muted">Carton sizes from the Dist Orders carton conversions</span>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>SKU</th><th>Units</th><th>Master</th><th>Inner</th><th>Single units</th><th className="lo-right">Total</th></tr></thead>
                    <tbody>
                      {cartonPlan.map(raw => {
                        const p = planRow(raw);
                        const total = planUnits(p);
                        const edit = (k, v) => setCartonEdits(ed => ({ ...ed, [p.key]: { ...(ed[p.key] || {}), [k]: v } }));
                        const tier = (k, size) => size
                          ? <><input type="number" min="0" className="lo-qty" value={p[k]} onChange={e => edit(k, e.target.value)} />
                              <span className="lo-muted"> ×{size}</span></>
                          : <span className="lo-muted">none</span>;
                        return (
                          <tr key={p.key}>
                            <td>{p.sku}</td>
                            <td>{p.quantity}</td>
                            <td>{tier('master_qty', p.master_size)}</td>
                            <td>{tier('inner_qty', p.inner_size)}</td>
                            <td><input type="number" min="0" className="lo-qty" value={p.unit_qty} onChange={e => edit('unit_qty', e.target.value)} /></td>
                            <td className={`lo-right ${total !== p.quantity ? 'status-warning' : ''}`}>
                              {total}{total !== p.quantity && ` (was ${p.quantity})`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="lo-muted lo-small-text">
                  Cartons are priced at their own Shopify price (e.g. some 10-pack inners carry a retail discount), so the
                  total can change. Percentage discounts carry over; per-unit amount discounts are multiplied by the carton size.
                </div>
                <div className="lo-row">
                  <span className="lo-grow" />
                  <button className="lo-link" onClick={() => { setCartonPlan(null); setCartonEdits({}); }}>Cancel</button>
                  <button className="lo-btn-primary lo-small" onClick={applyCartons}>Apply</button>
                </div>
              </div>
            )}

            {lines.length > 0 && (
              <div className="table-wrap lo-lines">
                <table>
                  <thead><tr><th>Product</th><th>Quantity</th><th className="lo-right">Total</th><th /></tr></thead>
                  <tbody>
                    {lines.map((l, i) => {
                      const short = shortBy[l.variant_id] || (l.carton && shortBy[l.carton.base_variant_id]);
                      const cl = calc?.lines?.[i];
                      return (
                        <tr key={l.key} className={short ? 'lo-short' : ''}>
                          <td className="lo-wrap">
                            <div className="lo-line-title">
                              {l.image ? <img src={l.image} alt="" /> : <span className="lo-noimg" />}
                              <div>
                                <div>
                                  {l.carton && <span className="lo-tag lo-carton-tag">{l.carton.tier} ×{l.carton.size}</span>}
                                  {l.title}{l.variant_title && <span className="lo-muted"> — {l.variant_title}</span>}
                                </div>
                                <div className="lo-muted">
                                  {l.custom ? 'Custom item' : l.sku} · {money(cl?.unit ?? l.price, cur)}
                                  {!l.custom && <> · <Avail v={l} location={location} /></>}
                                </div>
                                {short && (
                                  <div className="lo-warn-text">
                                    {location?.name} has {short.available} {short.sku} available, {short.requested} on this order{l.carton ? ' (all lines, in units)' : ''}
                                  </div>
                                )}
                                <button className="lo-link lo-small-text" onClick={() => setEditingDiscount(editingDiscount === l.key ? null : l.key)}>
                                  {describeDiscount(l.discount, cur) || 'Add discount'}{l.discount?.reason ? ` (${l.discount.reason})` : ''}
                                </button>
                                {editingDiscount === l.key && (
                                  <DiscountEditor perUnit value={l.discount} onChange={d => updateLine(l.key, { discount: d })}
                                                  onClose={() => setEditingDiscount(null)} />
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <input type="number" min="1" className="lo-qty" value={l.quantity}
                                   onChange={e => updateLine(l.key, { quantity: e.target.value })} />
                          </td>
                          <td className="lo-right">
                            {cl && cl.original !== cl.total && <div className="lo-strike">{money(cl.original, cur)}</div>}
                            {money(cl?.total, cur)}
                          </td>
                          <td><button className="lo-link" title="Remove" onClick={() => removeLine(l.key)}>✕</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Payment */}
          <div className="lo-card">
            <h2>Payment</h2>
            {calcError && <div className="error">{calcError}</div>}
            <div className="lo-summary">
              <div className="lo-sum-row"><span>Subtotal</span><span>{money(calc?.subtotal, cur)}</span></div>

              <div className="lo-sum-row">
                <button className="lo-link" onClick={() => setEditingDiscount(editingDiscount === 'order' ? null : 'order')}>
                  {orderDiscount?.value ? 'Edit order discount' : 'Add order discount'}
                </button>
                <span>{describeDiscount(orderDiscount, cur) || ''}{orderDiscount?.reason ? ` (${orderDiscount.reason})` : ''}</span>
              </div>
              {editingDiscount === 'order' && (
                <DiscountEditor value={orderDiscount} onChange={setOrderDiscount} onClose={() => setEditingDiscount(null)} />
              )}
              <div className="lo-sum-row lo-sub">
                <input className="lo-grow" placeholder="Discount codes (comma separated)" value={discountCodes}
                       onChange={e => setDiscountCodes(e.target.value)} />
                <label className="lo-check"><input type="checkbox" checked={autoDiscounts}
                  onChange={e => setAutoDiscounts(e.target.checked)} /> Automatic discounts</label>
              </div>
              <div className="lo-sum-row"><span>Discounts</span><span>{calc?.discounts && Number(calc.discounts) ? `−${money(calc.discounts, cur)}` : money(0, cur)}</span></div>

              <div className="lo-sum-row">
                <span>Shipping</span>
                <span>{money(calc?.shipping, cur)}</span>
              </div>
              <div className="lo-sum-row lo-sub">
                <select value={shipping.mode} onChange={e => setShipping({ ...shipping, mode: e.target.value })}>
                  <option value="none">No shipping line</option>
                  <option value="free">Free shipping</option>
                  <option value="custom">Custom rate</option>
                  <option value="rate">Shipping rate (needs address)</option>
                </select>
                {shipping.mode === 'custom' && (
                  <>
                    <input placeholder="Rate name" value={shipping.title} onChange={e => setShipping({ ...shipping, title: e.target.value })} />
                    <input type="number" min="0" step="0.01" placeholder={`Price (${currency})`} value={shipping.price}
                           onChange={e => setShipping({ ...shipping, price: e.target.value })} />
                  </>
                )}
                {shipping.mode === 'rate' && (
                  <select value={shipping.handle} onChange={e => setShipping({ ...shipping, handle: e.target.value })}>
                    <option value="">{calc?.shipping_rates?.length ? 'Pick a rate' : 'No rates — add a shipping address'}</option>
                    {(calc?.shipping_rates || []).map(r => (
                      <option key={r.handle} value={r.handle}>{r.title} — {money(r.price, r.currency)}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="lo-sum-row">
                <label className="lo-check"><input type="checkbox" checked={!taxExempt}
                  onChange={e => setTaxExempt(!e.target.checked)} /> Charge taxes</label>
                <span>{money(calc?.tax, cur)}{calc?.taxes_included ? ' (included)' : ''}</span>
              </div>
              <div className="lo-sum-row lo-total"><span>Total</span><span>{calculating ? '…' : money(calc?.total, cur)}</span></div>
              {calc?.warnings?.map(w => <div key={w} className="lo-warn-text">{w}</div>)}
            </div>

            <div className="lo-terms">
              <label className="lo-check">
                <input type="checkbox" checked={dueLater} onChange={e => setDueLater(e.target.checked)} />
                Payment due later
              </label>
              {dueLater && (
                <div className="lo-row">
                  <select value={termsId} onChange={e => setTermsId(e.target.value)}>
                    {meta?.payment_terms.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  {terms?.paymentTermsType === 'NET' && (
                    <label className="lo-inline">Issued <input type="date" value={issuedOn} onChange={e => setIssuedOn(e.target.value)} /></label>
                  )}
                  {terms?.paymentTermsType === 'FIXED' && (
                    <label className="lo-inline">Due <input type="date" value={dueOn} onChange={e => setDueOn(e.target.value)} /></label>
                  )}
                  {terms?.paymentTermsType === 'NET' && issuedOn && (
                    <span className="lo-muted">Due {dueDate(issuedOn, terms.dueInDays)}</span>
                  )}
                </div>
              )}
            </div>

            {shortfalls.length > 0 && (
              <div className="lo-oversell">
                <div className="lo-warn-text">
                  {location?.name} is short on {shortfalls.length} item{shortfalls.length === 1 ? '' : 's'}. The order
                  would wait on stock at this location or need moving later.
                </div>
                <label className="lo-check">
                  <input type="checkbox" checked={allowOversell} onChange={e => setAllowOversell(e.target.checked)} />
                  Create it anyway (oversell at {location?.name})
                </label>
              </div>
            )}

            <div className="lo-actions">
              <button className="lo-btn-secondary" disabled={!canSubmit} onClick={() => submit('draft')}>Save draft</button>
              <span className="lo-grow" />
              <button className="lo-btn-secondary" disabled={!canSubmit}
                      onClick={() => { setInvoice(inv => ({ ...inv, to: inv.to || email })); setInvoiceOpen(true); }}>
                Send invoice
              </button>
              <button className="lo-btn-primary" disabled={!canSubmit} onClick={() => submit('complete')}>
                {submitting ? 'Working…' : dueLater ? 'Create order' : 'Mark as paid'}
              </button>
            </div>
          </div>
        </div>

        <div className="lo-side">
          <CustomerSection customer={customer} onSelect={selectCustomer} />

          <div className="lo-card">
            <h2>Contact & shipping</h2>
            <input placeholder="Email (invoice / receipt)" value={email} onChange={e => setEmail(e.target.value)} />
            {customer?.addresses?.length > 0 && (
              <select onChange={e => { const a = customer.addresses.find(x => x.id === e.target.value); if (a) setShipAddr(stripAddress(a)); }}
                      value="">
                <option value="">Use a saved address…</option>
                {customer.addresses.map(a => <option key={a.id} value={a.id}>{addressLine(a)}</option>)}
              </select>
            )}
            <div className="lo-label">Shipping address</div>
            <AddressFields value={shipAddr} onChange={setShipAddr} />
            <label className="lo-check"><input type="checkbox" checked={billSame}
              onChange={e => setBillSame(e.target.checked)} /> Billing address same as shipping</label>
            {!billSame && (
              <>
                <div className="lo-label">Billing address</div>
                <AddressFields value={billAddr} onChange={setBillAddr} />
              </>
            )}
          </div>

          <div className="lo-card">
            <h2>Order details</h2>
            <label className="lo-label">Currency</label>
            <select value={currency} onChange={e => setCurrency(e.target.value)}>
              {(meta?.currencies || ['USD']).map(c => <option key={c}>{c}</option>)}
            </select>
            <label className="lo-label">PO number</label>
            <input value={poNumber} onChange={e => setPoNumber(e.target.value)} />
            <label className="lo-label">Notes</label>
            <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} />
            <label className="lo-label">Tags (comma separated)</label>
            <input value={tags} onChange={e => setTags(e.target.value)} placeholder="e.g. WHSL" />
            {location && (
              <div className="lo-muted lo-small-text">
                Also tagged <code>LOC-ORDER</code> and <code>Ship from {location.name}</code> automatically.
              </div>
            )}
          </div>
        </div>
      </div>

      {invoiceOpen && (
        <div className="lo-modal-backdrop" onClick={() => setInvoiceOpen(false)}>
          <div className="lo-modal" onClick={e => e.stopPropagation()}>
            <h2>Send invoice</h2>
            <label className="lo-label">To</label>
            <input value={invoice.to} onChange={e => setInvoice({ ...invoice, to: e.target.value })} />
            <label className="lo-label">Bcc (comma separated)</label>
            <input value={invoice.bcc} onChange={e => setInvoice({ ...invoice, bcc: e.target.value })} />
            <label className="lo-label">Subject</label>
            <input value={invoice.subject} placeholder="Shopify's default subject"
                   onChange={e => setInvoice({ ...invoice, subject: e.target.value })} />
            <label className="lo-label">Custom message</label>
            <textarea rows={4} value={invoice.message} onChange={e => setInvoice({ ...invoice, message: e.target.value })} />
            {dueLater && <div className="lo-muted lo-small-text">Payment terms ({terms?.name}) are saved on the draft.</div>}
            <div className="lo-actions">
              <button className="lo-btn-secondary" onClick={() => setInvoiceOpen(false)}>Cancel</button>
              <span className="lo-grow" />
              <button className="lo-btn-primary" disabled={!invoice.to.trim() || submitting} onClick={() => submit('invoice')}>
                {submitting ? 'Sending…' : 'Create draft & send invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div className="lo-recent">
          <h3>Recently created here</h3>
          <div className="table-wrap">
            <table>
              <thead><tr><th>When</th><th>Draft</th><th>Order</th><th>Ships from</th><th>What</th><th>By</th></tr></thead>
              <tbody>
                {recent.map(r => (
                  <tr key={r.draft.id}>
                    <td>{new Date(r.created_at).toLocaleString()}</td>
                    <td><a href={r.draft.url} target={ADMIN_LINK_TARGET} rel="noreferrer">{r.draft.name}</a></td>
                    <td>{r.order ? <a href={r.order.url} target={ADMIN_LINK_TARGET} rel="noreferrer">{r.order.name}</a> : '—'}</td>
                    <td>{r.location}</td>
                    <td>{{ draft: 'Saved draft', invoice: 'Invoice sent', complete: 'Order created' }[r.action]}
                        {r.errors.length > 0 && <span className="status-critical"> · error</span>}</td>
                    <td>{r.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function dueDate(issued, days) {
  const d = new Date(`${issued}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + (days || 0));
  return d.toISOString().slice(0, 10);
}

function Avail({ v, location }) {
  if (!v.tracked) return <span className="lo-muted">Not tracked</span>;
  if (v.available === null || v.available === undefined) return <span className="status-critical">Not stocked at {location?.name}</span>;
  const label = v.carton ? `${v.available} carton${v.available === 1 ? '' : 's'} available` : `${v.available} available`;
  return <span className={v.available > 0 ? 'status-ok' : 'status-critical'}>{label}</span>;
}

function ResultPanel({ result, onDismiss }) {
  const moves = result.moves || [];
  const blocked = moves.filter(m => m.result !== 'moved');
  return (
    <div className={`lo-result ${result.errors.length || blocked.length ? 'lo-result-warn' : ''}`}>
      <div className="lo-card-head">
        <strong>
          {result.action === 'complete' && result.order ? `Order ${result.order.name} created` :
           result.action === 'invoice' && result.invoice_sent_to ? `Invoice sent to ${result.invoice_sent_to}` :
           `Draft ${result.draft.name} saved`}
          {' '}· ships from {result.location}
        </strong>
        <button className="lo-link" onClick={onDismiss}>Dismiss</button>
      </div>
      <div className="lo-row">
        <a href={result.draft.url} target={ADMIN_LINK_TARGET} rel="noreferrer">Open draft {result.draft.name}</a>
        {result.order && <a href={result.order.url} target={ADMIN_LINK_TARGET} rel="noreferrer">Open order {result.order.name}</a>}
        <span className="lo-muted">{money(result.draft.total, result.draft.currency)}</span>
      </div>
      {moves.filter(m => m.result === 'moved').map(m => (
        <div key={m.fulfillment_order} className="lo-muted">Moved from {m.from} to {m.target}.</div>
      ))}
      {blocked.map(m => (
        <div key={m.fulfillment_order} className="lo-warn-text">Couldn't move from {m.from} to {m.target}: {m.detail}</div>
      ))}
      {result.errors.map(e => <div key={e} className="lo-warn-text">{e}</div>)}
      {result.action === 'invoice' && (
        <div className="lo-muted">When the customer pays, the order is moved to {result.location} automatically (checked every few minutes).</div>
      )}
    </div>
  );
}
